import { AfterViewInit, ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';

import { finalize, takeUntil, Subject, tap, catchError, filter, forkJoin, of, map, Observable, switchMap, take } from 'rxjs';
import { TranslocoService } from '@ngneat/transloco';

import { ISvcUserAvatar } from '@solvace/component/svc-user-avatar';
import { SvcDialogMode, SvcDialogService } from '@solvace/component/svc-dialogs';
import { SvcToastService } from '@solvace/component/svc-toast';
import { ActionPlanStatusMapping, ActionPlanStatusName, ActionPlanStatusType, ActionPlanStatusTyped } from './shared/enums/action-plan-status-type.enum';
import { SvcActionPlanList, SvcActionPlanPagedList } from './shared/interfaces/svc-actionplan-list.interface';
import { LegacyIntegrationService } from '@solvace/feature/integrations/legacy-integration.service';
import { SvcAccessPermissionService } from '../../admin/svc-access-permission/svc-access-permission.service';
import { UserRoleItem } from '../../admin/svc-access-permission/models/user-role.model';
import { HttpErrorService, SvcAppSettings } from 'projects/lib-shared-common/src/public-api';
import { AuthService } from 'projects/lib-shared-core/src/lib/auth/auth.service';
import { SVC_ACTION_PLAN_MESSAGES } from './shared/utils/svc-actionplan-config';
import { SvcPaginateEvent } from '@solvace/component/svc-paginate';
import { SvcActionPlanService } from './shared/services/svc-action-plan.service';
import { SvcModalRcaAdditionalReferenceId } from '../../modals/svc-modal-rca/shared/enums/svc-modal-rca-status.enum';
import { MatDialog } from '@angular/material/dialog';
import { SvcModalActionPlanComponent } from '../../modals/svc-modal-action-plan/svc-modal-action-plan.component';
import { SvcModalActionPlanConfig } from '../../modals/svc-modal-action-plan/shared/interfaces/svc-modal-action-plan.interface';
import { SvcActionPlanSourceRedirectService } from '../../modals/svc-modal-action-plan/shared/services/svc-action-plan-source-redirect.service';

const ACTION_PLAN_MODAL_SIZE = { width: '800px', maxWidth: '800px', height: '1000px', maxHeight: '1000px' } as const;
/** Default reference type ("Main Modal Id") used when creating a plan from a host module. RCA overrides it per section. */
const DEFAULT_ACTION_PLAN_REFERENCE_TYPE_ID = 3;
/** Draft reference value range: an 8-digit number used until the parent record exists. */
const DRAFT_REFERENCE_MIN = 10_000_000;
const DRAFT_REFERENCE_RANGE = 90_000_000;
const PENDING_PLANS_FETCH_SIZE = 100;
const PENDING_PLAN_STATUSES: number[] = [ActionPlanStatusTyped.late, ActionPlanStatusTyped.ontime, ActionPlanStatusTyped.approval];

@Component({
  selector: 'svc-actionplan',
  templateUrl: './svc-actionplan.component.html',
  styleUrls: ['./svc-actionplan.component.scss'],
  providers: [SvcActionPlanService],
})
export class SvcActionplanComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @Input() public id: number;
  @Input() public originatorId: string;
  @Input() public rolesCreatePermission: string[] = [];
  @Input() public canCreate?: boolean;
  @Input() public isViewMode: boolean;
  @Input() public applicationId: string = this._appSettings.applicationId;
  @Input() public additionalReferenceId = SvcModalRcaAdditionalReferenceId.ActionPlan;
  /**
   * Backend reference type sent on create (`systemReferences.referenceTypeId`).
   * Defaults to the module "Main Modal Id" (3); RCA passes its section value
   * (1 = immediate action plans, 2 = action plans).
   */
  @Input() public referenceTypeId = DEFAULT_ACTION_PLAN_REFERENCE_TYPE_ID;
  @Input() public legacyApplicationId?: string;
  /**
   * Secondary reference types listed alongside the primary one, paired by index
   * with `additionalReferenceIds`. Used by RCA "Action Plans" to also list
   * WhyWhy plans (referenceTypeId 4). Not used on create.
   */
  @Input() public referenceTypeIds: number[] = [];
  /**
   * Forces the draft state of plans created from here. When omitted, it is
   * derived from whether the parent record exists (no parent id ⇒ draft).
   */
  @Input() public isDraft?: boolean;
  @Input() public additionalReferenceId2?: number;
  @Input() public additionalReferenceIds: number[] = [];
  @Input() public showSearch = true;
  @Input() public classLoading = 'py-30';
  /**
   * When true, clicking the "+" button does NOT open the legacy action plan
   * dialog; instead it emits {@link addActionPlan} so the host can open its
   * own modal. Opening an existing plan (card click) is unaffected.
   */
  @Input() public addExternally = false;

  @Output() public openPlans = new EventEmitter<number>();
  @Output() public completePlans = new EventEmitter<number>();
  @Output() public actionPlanUpdated = new EventEmitter<void>();
  @Output() public actionPlanDialogBusy = new EventEmitter<boolean>();
  @Output() public addActionPlan = new EventEmitter<void>();

  public actionsPlans: SvcActionPlanList[] = [];
  public isLoading: boolean;
  public userAvatar: ISvcUserAvatar;
  public actionPlanStatusType: ActionPlanStatusMapping = ActionPlanStatusType;
  public actionPlanStatusName: ActionPlanStatusMapping = ActionPlanStatusName;
  public showButtonAddActionplan: boolean;
  public tempId: number;
  public messagesToast = SVC_ACTION_PLAN_MESSAGES;
  public searchText = '';
  public pageIndex = 1;
  public totalRegisters: number;
  public readonly pageSize = 10;

  @Input() public hidePaginationOnSinglePage = false;

  public get showPagination(): boolean {
    if (!this.actionsPlans?.length) return false;
    return !this.hidePaginationOnSinglePage || this.totalRegisters > this.pageSize;
  }

  /** Stable map of draft `planId` → sequential "Rascunho N" number for the current creation session. */
  private readonly _draftNumbers = new Map<number, number>();
  /** Last assigned draft number; never decremented so removed drafts are not reused (rule 6.3). */
  private _draftCounter = 0;
  private _activeComponentId: string;
  private _lastLegacyCallbackKey = '';
  private _lastLegacyCallbackAt = 0;
  private _rolesUser: UserRoleItem[] = [];
  private _isDialogBusy = false;
  private _unlockAfterLoading = false;
  private _dialogUnlockTimer?: ReturnType<typeof setTimeout>;
  private readonly _dialogUnlockDelayMs = 1000;
  private _iframeMutationObserver: MutationObserver;
  private readonly _unsubscribe$ = new Subject<void>();

  constructor(
    private readonly _actionPlanService: SvcActionPlanService,
    private readonly _legacyService: LegacyIntegrationService,
    private readonly _toastService: SvcToastService,
    private readonly _translocoService: TranslocoService,
    private readonly _accessPermissionService: SvcAccessPermissionService,
    private readonly _httpErrorService: HttpErrorService,
    private readonly _authService: AuthService,
    private readonly _dialogService: SvcDialogService,
    private readonly _appSettings: SvcAppSettings,
    private readonly _dialog: MatDialog,
    private readonly _cdr: ChangeDetectorRef,
    private readonly _sourceRedirectService: SvcActionPlanSourceRedirectService,
  ) {
    this.setFunctionShowSimpleToast();
  }

  public ngOnInit(): void {
    this.getRolesApplication();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    const idChanged = changes?.id?.previousValue !== changes?.id?.currentValue;
    const hasIdValue = changes?.id?.currentValue !== undefined && changes?.id?.currentValue !== null;
    const originatorChanged = changes?.originatorId?.previousValue !== changes?.originatorId?.currentValue;
    const rolesPermissionChanged = changes?.rolesCreatePermission?.previousValue !== changes?.rolesCreatePermission?.currentValue;
    const canCreateChanged = changes?.canCreate?.previousValue !== changes?.canCreate?.currentValue;
    const additionalRef2Changed = changes?.additionalReferenceId2?.previousValue !== changes?.additionalReferenceId2?.currentValue;

    if (idChanged && hasIdValue) {
      this._resetFilters();
      // When the parent record gets its real id, publish (convert to definitive)
      // any drafts created against the temporary reference; otherwise just load.
      if (this.tempId) this._publishDraftPlans();
      else this.getActionsPlans(true);
    }

    if (originatorChanged || rolesPermissionChanged || canCreateChanged) this.setShowButtonAddActionplan();
    if (!idChanged && hasIdValue && additionalRef2Changed) this.getActionsPlans(true);
  }

  public ngAfterViewInit(): void {
    this.observeChangesOnModalLegacy();
  }

  public ngOnDestroy(): void {
    this._unsubscribe$.next();
    this._unsubscribe$.complete();
    this._disconnectIframeObserver();
    this._clearDialogUnlockTimer();
    if (window?.['showSimpleToast']) {
      window['showSimpleToast'] = undefined;
    }
  }

  private getRolesApplication(): void {
    this._accessPermissionService
      .getRolesByUser(this.applicationId)
      .pipe(
        tap((rolesUser: UserRoleItem[]) => {
          this._rolesUser = rolesUser ?? [];
          this.setShowButtonAddActionplan();
        }),
        catchError((err) => {
          this._httpErrorService.showErrorInToast(err);
          return of([]);
        }),
        takeUntil(this._unsubscribe$)
      )
      .subscribe();
  }

  private _resetFilters(): void {
    this.searchText = '';
    this.pageIndex = 1;
    this.totalRegisters = 0;
    this.actionsPlans = [];
    this.openPlans.emit(0);
    this.completePlans.emit(0);
  }

  private _hasReference(): boolean {
    return this.id !== undefined && this.id !== null ? true : !!this.tempId;
  }

  private _mapRolesToLowerCase(): string[] {
    return this._rolesUser?.map((role: UserRoleItem) => role?.roleId?.toLowerCase()) ?? [];
  }

  private setShowButtonAddActionplan(): void {
    if (this.canCreate !== undefined) {
      this.showButtonAddActionplan = this.canCreate;
      return;
    }
    const rolesCreatePermission = this.rolesCreatePermission?.map((role) => role?.toLowerCase()) ?? [];
    const rolesUser = this._mapRolesToLowerCase();
    this.showButtonAddActionplan =
      rolesUser?.some((role: string) => rolesCreatePermission?.includes(role)) ||
      (this.originatorId && this._authService.userId && this.originatorId?.toLowerCase() === this._authService.userId?.toLowerCase());
  }

  private setFunctionShowSimpleToast(): void {
    window['showSimpleToast'] = (message: string): string => message;
  }

  public getActionsPlans(isFirstTime = false): void {
    if (!this._hasReference()) {
      this.isLoading = false;
      return;
    }

    if (this.isLoading && !isFirstTime) return;

    // `referenceTypeId` and `additionalReferenceId` travel paired by index:
    // index 0 is the primary section, the rest are secondary references
    // (e.g. RCA "Action Plans" also lists WhyWhy plans).
    const referenceTypeIds = [this.referenceTypeId, ...(this.referenceTypeIds ?? [])].filter((id) => id !== null && id !== undefined);
    const additionalReferenceIds = [this.additionalReferenceId, ...(this.additionalReferenceIds ?? [])].filter((id) => id !== null && id !== undefined);

    if (!referenceTypeIds.length) {
      this.isLoading = false;
      return;
    }

    // Drafts are only listed while there is no real parent record (querying by
    // the temp reference). Once the parent exists, list the definitive plans.
    const listingDrafts = this.id === undefined || this.id === null;
    const backendSearch = listingDrafts ? '' : this.searchText;
    this.isLoading = true;
    this._actionPlanService
      .isModuleActive(this.legacyApplicationId)
      .pipe(
        take(1),
        switchMap((legacyActive) =>
          legacyActive ? this._getLinkedList(additionalReferenceIds, backendSearch) : this._getSearchList(referenceTypeIds, additionalReferenceIds, listingDrafts, backendSearch)
        ),
        catchError((err) => {
          this._httpErrorService.showErrorInToast(err);
          return of({ data: [], totalCount: 0, completedTotalCount: 0 } as SvcActionPlanPagedList);
        }),
        finalize(() => {
          this.isLoading = false;
          if (this._unlockAfterLoading) {
            this._unlockAfterLoading = false;
            this._setDialogBusy(false);
          }
        }),
        takeUntil(this._unsubscribe$)
      )
      .subscribe({
        next: (actionsPlans: SvcActionPlanPagedList) => {
          const totalCount = actionsPlans?.totalCount ?? 0;
          this.actionsPlans = actionsPlans?.data ?? [];
          this._assignDraftNumbers(this.actionsPlans);
          if (listingDrafts) this.actionsPlans = this._filterDraftsByNumber(this.actionsPlans);
          this.totalRegisters = listingDrafts ? this.actionsPlans.length : totalCount;
          this.openPlans.emit(totalCount);
          this.completePlans.emit(actionsPlans?.completedTotalCount ?? 0);
        },
      });
  }

  public countPendingPlans(): Observable<number> {
    const referenceValue = this.id;
    const referenceTypeIds = [this.referenceTypeId, ...(this.referenceTypeIds ?? [])].filter((id) => id !== null && id !== undefined);
    const additionalReferenceIds = [this.additionalReferenceId, ...(this.additionalReferenceIds ?? [])].filter((id) => id !== null && id !== undefined);

    if (referenceValue === undefined || referenceValue === null || !referenceTypeIds.length) return of(0);

    return this._actionPlanService.isModuleActive(this.legacyApplicationId).pipe(
      take(1),
      switchMap((legacyActive) => {
        const sources = legacyActive
          ? additionalReferenceIds.map((additionalReferenceId) =>
              this._actionPlanService.getLinkedActionsPlans(referenceValue, this.applicationId, 1, additionalReferenceId, PENDING_PLANS_FETCH_SIZE, '')
            )
          : referenceTypeIds.map((referenceTypeId, index) =>
              this._actionPlanService.getActionsPlans(referenceValue, this.applicationId, 1, [referenceTypeId], [additionalReferenceIds[index]], false, PENDING_PLANS_FETCH_SIZE, '')
            );
        return sources.length ? forkJoin(sources) : of([] as SvcActionPlanPagedList[]);
      }),
      map((pagedLists) => {
        const pendingPlanIds = new Set<number>();
        pagedLists
          .flatMap((pagedList) => pagedList?.data ?? [])
          .filter((plan) => PENDING_PLAN_STATUSES.includes(plan?.statusId))
          .forEach((plan) => pendingPlanIds.add(plan.planId));
        return pendingPlanIds.size;
      })
    );
  }

  /**
   * Lists plans for the reference pairs. The search endpoint only honors a
   * single `referenceTypeId`/`additionalReferenceId` pair per request, so when
   * secondary pairs exist (RCA "Action Plans" consolidating WhyWhy plans) each
   * pair is fetched with its own request and the results are merged here.
   */
  private _getSearchList(referenceTypeIds: number[], additionalReferenceIds: number[], listingDrafts: boolean, backendSearch: string): Observable<SvcActionPlanPagedList> {
    const referenceValue = this.id ?? this.tempId;
    if (referenceTypeIds.length === 1) {
      return this._actionPlanService.getActionsPlans(referenceValue, this.applicationId, this.pageIndex, referenceTypeIds, additionalReferenceIds, listingDrafts, this.pageSize, backendSearch);
    }

    // Each source is fetched up to the current page window so the merged,
    // re-sorted list can slice the exact global page.
    const fetchSize = this.pageIndex * this.pageSize;
    return forkJoin(
      referenceTypeIds.map((referenceTypeId, index) =>
        this._actionPlanService.getActionsPlans(referenceValue, this.applicationId, 1, [referenceTypeId], [additionalReferenceIds[index]], listingDrafts, fetchSize, backendSearch)
      )
    ).pipe(map((pagedLists) => this._mergePagedLists(pagedLists)));
  }

  /**
   * Lists legacy-linked plans for each additional reference id. The legacy
   * endpoint only honors a single `additionalReferenceId` per request, so when
   * secondary pairs exist (RCA "Action Plans" consolidating WhyWhy plans) each
   * one is fetched with its own request and the results are merged here.
   */
  private _getLinkedList(additionalReferenceIds: number[], backendSearch: string): Observable<SvcActionPlanPagedList> {
    const referenceValue = this.id ?? this.tempId;
    if (additionalReferenceIds.length === 1) {
      return this._actionPlanService.getLinkedActionsPlans(referenceValue, this.applicationId, this.pageIndex, additionalReferenceIds[0], this.pageSize, backendSearch);
    }

    // Each source is fetched up to the current page window so the merged,
    // re-sorted list can slice the exact global page.
    const fetchSize = this.pageIndex * this.pageSize;
    return forkJoin(
      additionalReferenceIds.map((additionalReferenceId) =>
        this._actionPlanService.getLinkedActionsPlans(referenceValue, this.applicationId, 1, additionalReferenceId, fetchSize, backendSearch)
      )
    ).pipe(map((pagedLists) => this._mergePagedLists(pagedLists)));
  }

  private _mergePagedLists(pagedLists: SvcActionPlanPagedList[]): SvcActionPlanPagedList {
    const seenPlanIds = new Set<number>();
    const merged = pagedLists
      .flatMap((pagedList) => pagedList?.data ?? [])
      .filter((plan) => {
        if (plan?.planId === null || plan?.planId === undefined) return true;
        if (seenPlanIds.has(plan.planId)) return false;
        seenPlanIds.add(plan.planId);
        return true;
      })
      .sort((first, second) => (second?.planId ?? 0) - (first?.planId ?? 0));

    const pageStart = (this.pageIndex - 1) * this.pageSize;
    return {
      totalCount: pagedLists.reduce((total, pagedList) => total + (pagedList?.totalCount ?? 0), 0),
      completedTotalCount: pagedLists.reduce((total, pagedList) => total + (pagedList?.completedTotalCount ?? 0), 0),
      data: merged.slice(pageStart, pageStart + this.pageSize),
    } as SvcActionPlanPagedList;
  }

  /**
   * Assigns the sequential "Rascunho N" number to each draft while the parent
   * record does not exist yet. Numbers are stable per draft: a draft keeps the
   * number it first received, and removed drafts are never reused or reordered
   * (rule 6.3). New drafts are numbered by creation order (ascending `planId`,
   * since the backend assigns ids monotonically on create), so the earliest
   * added draft gets the lowest number regardless of the list sort order.
   */
  private _assignDraftNumbers(plans: SvcActionPlanList[]): void {
    const isListingDrafts = (this.id === undefined || this.id === null) && !!this.tempId;
    if (!isListingDrafts) return;

    plans
      .filter((plan) => plan?.planId !== null && plan?.planId !== undefined && !this._draftNumbers.has(plan.planId))
      .sort((first, second) => (first.planId ?? 0) - (second.planId ?? 0))
      .forEach((plan) => this._draftNumbers.set(plan.planId, ++this._draftCounter));

    plans.forEach((plan) => (plan.draftNumber = this._draftNumbers.get(plan?.planId)));
  }

  private _filterDraftsByNumber(plans: SvcActionPlanList[]): SvcActionPlanList[] {
    const term = (this.searchText ?? '').trim();
    if (!term) return plans;
    return plans.filter((plan) => String(plan?.draftNumber ?? '').includes(term));
  }

  private updateActionsPlans(messageKey: string): void {
    if (this._shouldIgnoreDuplicatedLegacyCallback(messageKey)) return;

    const message: string = this.messagesToast.get(messageKey);

    if (message) this._toastService.success(this._translocoService.translate(message));

    if (this.messagesToast?.has(messageKey)) {
      this._clearDialogUnlockTimer();
      this._unlockAfterLoading = true;
      if (!this._isDialogBusy) this._setDialogBusy(true);
      this.getActionsPlans();
      this.actionPlanUpdated.emit();
    }
  }

  private _shouldIgnoreDuplicatedLegacyCallback(messageKey: string): boolean {
    const callbackKey = `${this._activeComponentId}:${messageKey}`;
    const now = Date.now();
    const isDuplicatedCallback = this._lastLegacyCallbackKey === callbackKey && now - this._lastLegacyCallbackAt < 10000;

    this._lastLegacyCallbackKey = callbackKey;
    this._lastLegacyCallbackAt = now;

    return isDuplicatedCallback;
  }

  private observeChangesOnModalLegacy(): void {
    this._legacyService.onLegacyCallback$
      .pipe(
        filter(
          (_e): _e is CustomEvent =>
            !!this._activeComponentId &&
            this._actionPlanService.activeComponentId === this._activeComponentId &&
            SvcActionPlanService.lastOpenedComponentId === this._activeComponentId
        ),
        tap((customEvent: CustomEvent) => {
          // AB#73048 the legacy modal Source link opens the parent record modal on top (stacked)
          if (customEvent?.detail?.functionName === 'openActionPlanSourceModal') {
            this._openSourceAboveLegacyModal(customEvent.detail.data?.url);
            return;
          }
          this.updateActionsPlans(customEvent?.detail?.functionName);
        }),
        takeUntil(this._unsubscribe$)
      )
      .subscribe();
  }

  /**
   * Opens the record behind the legacy modal Source link, stacked on top of it. The legacy
   * modal iframe sits at z-index 99999, so the Angular overlays are lifted above it while
   * the opened modal lives, then restored.
   */
  private _openSourceAboveLegacyModal(url?: string): void {
    void this._sourceRedirectService.openNotificationSource(url).then((dialogRef) => {
      const overlayContainer = document.querySelector('.cdk-overlay-container') as HTMLElement | null;
      if (!dialogRef || !overlayContainer) return;

      const legacyHost = document.querySelector('#legacy-modal-support') as HTMLElement | null;
      const previousZIndex = overlayContainer.style.zIndex;
      overlayContainer.style.zIndex = String(Math.max(this._zIndexOf(legacyHost) + 1, 100000));
      // No takeUntil: the overlay must be restored even if this host is destroyed first.
      dialogRef
        .afterClosed()
        .pipe(take(1))
        .subscribe(() => (overlayContainer.style.zIndex = previousZIndex));
    });
  }

  /**
   * AB#73048 a legacy modal opened from an Angular modal stacked above the legacy host (the
   * action plan tab of that modal) must show on top of it: lift the host above the overlay
   * until the legacy modal closes.
   */
  private _liftLegacyModalAboveOverlay(): void {
    const legacyHost = document.querySelector('#legacy-modal-support') as HTMLElement | null;
    const overlayContainer = document.querySelector('.cdk-overlay-container') as HTMLElement | null;
    if (!legacyHost || !overlayContainer) return;

    const overlayZIndex = this._zIndexOf(overlayContainer);
    if (this._zIndexOf(legacyHost) > overlayZIndex) return;

    const previousZIndex = legacyHost.style.zIndex;
    let shown = legacyHost.style.display === 'block';
    const observer = new MutationObserver(() => {
      if (legacyHost.style.display === 'block') shown = true;
      else if (shown && legacyHost.style.display === 'none') {
        observer.disconnect();
        legacyHost.style.zIndex = previousZIndex;
      }
    });
    observer.observe(legacyHost, { attributes: true, attributeFilter: ['style'] });
    legacyHost.style.zIndex = String(overlayZIndex + 1);
  }

  private _zIndexOf(element: HTMLElement | null): number {
    const zIndex = element ? Number(element.style.zIndex || getComputedStyle(element).zIndex) : Number.NaN;
    return Number.isFinite(zIndex) ? zIndex : 0;
  }

  public openDialogActionPlan(actionPlan: SvcActionPlanList = null, isEdit = true, paramPlanName?: string): void {
    if (!actionPlan && this.addExternally) {
      this.addActionPlan.emit();
      return;
    }

    if (actionPlan?.classified && !actionPlan?.allowed) {
      this._dialogService.openWarning(this._translocoService.translate('Registro de acesso restrito'));
      return;
    }

    this._actionPlanService
      .isModuleActive(this.legacyApplicationId)
      .pipe(take(1), takeUntil(this._unsubscribe$))
      .subscribe((legacyActive) => {
        if (legacyActive) this._openLegacyModal(actionPlan, isEdit, paramPlanName);
        else this._openAngularModal(actionPlan, isEdit);
      });
  }

  private _openAngularModal(actionPlan: SvcActionPlanList | null, isEdit: boolean): void {
    if (actionPlan) {
      const mode = isEdit ? SvcDialogMode.edit : SvcDialogMode.view;
      const isDraft = this.isDraft ?? (this.id === undefined || this.id === null);
      this._openActionPlanModal({ mode, id: actionPlan.planId, isDraft });
      return;
    }

    this._setDialogBusy(true);
    this._resolveLockedSystemUniqueId()
      .pipe(takeUntil(this._unsubscribe$))
      .subscribe((lockedSystemUniqueId) => {
        this._openActionPlanModal({
          mode: SvcDialogMode.add,
          ...this._buildCreationConfig(),
          ...(lockedSystemUniqueId ? { lockedSystemUniqueId } : {}),
        });
      });
  }

  private _openLegacyModal(actionPlan: SvcActionPlanList | null, isEdit: boolean, paramPlanName?: string): void {
    const idBuffer = new Uint32Array(2);
    globalThis.crypto.getRandomValues(idBuffer);
    this._activeComponentId = `${idBuffer[0]}-${idBuffer[1]}`;
    this._actionPlanService.activeComponentId = this._activeComponentId;
    SvcActionPlanService.lastOpenedComponentId = this._activeComponentId;

    const isDraft = this.id === undefined || this.id === null;
    if (isDraft) this._ensureDraftReference();

    this._actionPlanService.openDialogActionPlanLegacy(
      this.id ?? this.tempId,
      actionPlan?.planId ?? 0,
      isEdit ?? false,
      isDraft,
      this.additionalReferenceId,
      paramPlanName,
      this.applicationId,
      this.additionalReferenceId2
    );

    this._liftLegacyModalAboveOverlay();
    this._unlockAfterLoading = false;
    this._clearDialogUnlockTimer();
    this._setDialogBusy(true);
    this._watchDialogClose();
  }

  /** Opens the Angular action plan modal and refreshes the list when it closes. */
  private _openActionPlanModal(config: SvcModalActionPlanConfig): void {
    this._setDialogBusy(true);
    this._dialog
      .open(SvcModalActionPlanComponent, {
        ...ACTION_PLAN_MODAL_SIZE,
        autoFocus: false,
        disableClose: true,
        data: config,
      })
      .afterClosed()
      .pipe(takeUntil(this._unsubscribe$))
      .subscribe(() => {
        this._setDialogBusy(false);
        this.getActionsPlans(true);
        this.actionPlanUpdated.emit();
      });
  }

  /**
   * Resolves the `systemUniqueId` that owns this module's application in the
   * current site, used to pre-select and lock the "Sistema" field. Returns
   * `null` when it cannot be resolved (missing inputs or lookup failure) so the
   * modal still opens with the field unlocked.
   */
  private _resolveLockedSystemUniqueId(): Observable<number | null> {
    const siteId = this._actionPlanService.getCurrentSiteId();
    if (!this.applicationId || !siteId) return of(null);
    return this._actionPlanService.getSystemByApplication(this.applicationId, siteId).pipe(
      map((systems) => {
        const match = systems.find((system) => system?.isActive && system?.systemUniqueId) ?? systems.find((system) => system?.systemUniqueId);
        return match?.systemUniqueId ?? null;
      })
    );
  }

  /**
   * Builds the creation payload mirroring the notification URL contract: a
   * single system reference with the fixed `referenceTypeId` and a
   * `referenceValue` that is the parent record id for a definitive plan, or a
   * generated draft reference for a draft. `isDraft` is taken from the
   * {@link isDraft} input when the host sets it; otherwise it is derived from
   * whether the parent record already exists.
   */
  private _buildCreationConfig(): Pick<SvcModalActionPlanConfig, 'notificationParams' | 'isDraft'> {
    const hasParentRecord = this.id !== undefined && this.id !== null;
    const isDraft = this.isDraft ?? !hasParentRecord;
    const referenceValue = isDraft ? this._ensureDraftReference() : this.id;

    return {
      isDraft,
      notificationParams: {
        applicationId: this.applicationId,
        referenceValue,
        referenceTypeId: this.referenceTypeId,
        systemReferences: [{ referenceValue, referenceTypeId: this.referenceTypeId }],
      },
    };
  }

  /** Reuses or generates the draft reference, persisting it on `tempId` so a later parent id can promote the drafts. */
  private _ensureDraftReference(): number {
    if (!this.tempId) this.tempId = this._generateDraftReference();
    return this.tempId;
  }

  private _generateDraftReference(): number {
    const buffer = new Uint32Array(1);
    globalThis.crypto.getRandomValues(buffer);
    return DRAFT_REFERENCE_MIN + (buffer[0] % DRAFT_REFERENCE_RANGE);
  }

  private _setDialogBusy(busy: boolean): void {
    this._isDialogBusy = busy;
    this.actionPlanDialogBusy.emit(busy);
  }

  private _watchDialogClose(): void {
    this._disconnectIframeObserver();
    const iframe = document.querySelector('#legacy-modal-support') as HTMLElement;
    if (!iframe) {
      this._scheduleDialogUnlock();
      return;
    }

    this._iframeMutationObserver = new MutationObserver(() => {
      if (iframe.style.display === 'none') {
        this._disconnectIframeObserver();
        this._refreshAfterLegacyClose();
      }
    });
    this._iframeMutationObserver.observe(iframe, { attributes: true, attributeFilter: ['style'] });
  }

  private _refreshAfterLegacyClose(): void {
    this._clearDialogUnlockTimer();

    if (!this._hasReference()) {
      this._setDialogBusy(false);
      return;
    }

    this._unlockAfterLoading = true;
    this.getActionsPlans(true);
    this.actionPlanUpdated.emit();
  }

  private _scheduleDialogUnlock(): void {
    this._clearDialogUnlockTimer();

    this._dialogUnlockTimer = setTimeout(() => {
      if (this.isLoading || this._unlockAfterLoading) {
        this._unlockAfterLoading = true;
        return;
      }

      this._setDialogBusy(false);
    }, this._dialogUnlockDelayMs);
  }

  private _clearDialogUnlockTimer(): void {
    if (this._dialogUnlockTimer) {
      clearTimeout(this._dialogUnlockTimer);
      this._dialogUnlockTimer = undefined;
    }
  }

  private _disconnectIframeObserver(): void {
    this._iframeMutationObserver?.disconnect();
    this._iframeMutationObserver = null;
  }

  public filterActionsPlans(text: string): void {
    this.searchText = text;
    this.pageIndex = 1;
    this.getActionsPlans();
  }

  public getDelayedDaysLabel(actionPlan: SvcActionPlanList): string {
    const statusId = Number(actionPlan?.statusId);
    if (statusId === ActionPlanStatusTyped.completed || statusId === ActionPlanStatusTyped.inactive) return '-';
    const days = Number(actionPlan?.delayedDays);
    const safeDays = Number.isFinite(days) ? days : 0;
    return `${safeDays} ${this._translocoService.translate(safeDays > 1 ? 'dias' : 'dia')}`;
  }

  /**
   * Converts the drafts created against the temporary reference into definitive
   * plans once the parent record exists. Resolves the system, then issues a
   * single `POST /ActionPlan/publish/by-reference` re-pointing the drafts from
   * the temporary reference (`oldReferenceValue`) to the real parent id
   * (`newReferenceValue`). Refreshes the list afterwards.
   */
  private _publishDraftPlans(): void {
    if (!this.tempId || this.id === undefined || this.id === null) {
      this.getActionsPlans(true);
      return;
    }

    this._actionPlanService
      .isModuleActive(this.legacyApplicationId)
      .pipe(take(1), takeUntil(this._unsubscribe$))
      .subscribe((legacyActive) => (legacyActive ? this._publishDraftPlansLegacy() : this._publishDraftPlansRevamp()));
  }

  private _publishDraftPlansLegacy(): void {
    this.isLoading = true;
    this._actionPlanService
      .insertPlanFromDraftPlan(this.tempId, this.id, this.applicationId)
      .pipe(
        catchError((err) => {
          this._httpErrorService.showErrorInToast(err);
          return of(null);
        }),
        finalize(() => {
          this.tempId = null;
          this._draftNumbers.clear();
          this._draftCounter = 0;
          this.getActionsPlans(true);
        }),
        takeUntil(this._unsubscribe$)
      )
      .subscribe();
  }

  private _publishDraftPlansRevamp(): void {
    const oldReferenceValue = this.tempId;
    const newReferenceValue = this.id;
    const referenceTypeIds = [this.referenceTypeId, ...(this.referenceTypeIds ?? [])].filter((id) => id !== null && id !== undefined);

    this.isLoading = true;
    this._resolveLockedSystemUniqueId()
      .pipe(
        switchMap((systemUniqueId) =>
          systemUniqueId
            ? this._actionPlanService
                .publishDraftsByReference({ systemUniqueId, oldReferenceValue, newReferenceValue, referenceTypeIds })
                .pipe(
                  catchError((err) => {
                    this._httpErrorService.showErrorInToast(err);
                    return of(null);
                  })
                )
            : of(null)
        ),
        finalize(() => {
          this.tempId = null;
          this._draftNumbers.clear();
          this._draftCounter = 0;
          this.getActionsPlans(true);
        }),
        takeUntil(this._unsubscribe$)
      )
      .subscribe();
  }

  public onPageChange(event: SvcPaginateEvent): void {
    this.pageIndex = event.page;
    this.getActionsPlans();
  }
}